import React, { useState, useEffect } from 'react';
import styles from '../styles/DateRangeSelector.module.css';
import '../styles/theme-variables.css';
import {
  Box, Button, FormControl, InputLabel, MenuItem, Select,
  TextField, Typography, Alert, Tooltip, IconButton
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import moment from 'moment';
import 'moment-timezone';
import AuditService from '../../../services/audit.services';
import logger from '../../../utils/logger';

const DateRangeSelector = ({ onDateRangeChange, initialStartDate, initialEndDate }) => {
  const [timezone, setTimezone] = useState('UTC');
  const [isTritonMember, setIsTritonMember] = useState(false);
  // Removed isValidTimezone since it's not used
  const [startDate, setStartDate] = useState(initialStartDate || '');
  const [endDate, setEndDate] = useState(initialEndDate || '');
  const [preset, setPreset] = useState('custom');
  const [usingBrowserTimezone, setUsingBrowserTimezone] = useState(false);
  // Removed profileData state since it's not used

  moment.updateLocale('en', { week: { dow: 1 } });

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userData = await AuditService.getCurrentUser();
        logger.info('User profile data:', userData);
        const isTriton = userData?.groups?.includes('triton');
        logger.info('Is Triton member:', isTriton, 'Groups:', userData?.groups);
        setIsTritonMember(isTriton);
        
        if (userData?.timezone) {
          try {
            moment.tz(userData.timezone).format();
            setTimezone(userData.timezone);
            setUsingBrowserTimezone(false);
          } catch {
            console.error('Invalid timezone:', userData.timezone);
            // Fallback to browser's local timezone
            const browserTimezone = moment.tz.guess();
            logger.info('Falling back to browser timezone:', browserTimezone);
            setTimezone(browserTimezone);
            setUsingBrowserTimezone(true);
          }
        } else {
          // If no timezone in profile, use the browser's local timezone
          const browserTimezone = moment.tz.guess();
          logger.info('No timezone in profile, using browser timezone:', browserTimezone);
          setTimezone(browserTimezone);
          setUsingBrowserTimezone(true);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Fallback to browser's timezone in case of error
        const browserTimezone = moment.tz.guess();
        logger.info('Error fetching profile, using browser timezone:', browserTimezone);
        setTimezone(browserTimezone);
        setUsingBrowserTimezone(true);
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    logger.info('Setting initial dates with isTritonMember:', isTritonMember);
    if (!initialStartDate) {
      // Both triton and non-triton members default to 90 days view initially
      const defaultDays = 90;
      setStartDate(moment().tz(timezone).subtract(defaultDays, 'days').format('YYYY-MM-DD'));
    }
    if (!initialEndDate) {
      setEndDate(moment().tz(timezone).format('YYYY-MM-DD'));
    }
  }, [timezone, initialStartDate, initialEndDate, isTritonMember]);

  useEffect(() => {
    if (!startDate) {
      setStartDate(moment().tz(timezone).subtract(90, 'days').format('YYYY-MM-DD'));
    }
    if (!endDate) {
      setEndDate(moment().tz(timezone).format('YYYY-MM-DD'));
    }
  }, [timezone, startDate, endDate]);

  useEffect(() => {
    if (initialStartDate && initialStartDate !== startDate) setStartDate(initialStartDate);
    if (initialEndDate && initialEndDate !== endDate) setEndDate(initialEndDate);
  }, [initialStartDate, initialEndDate, startDate, endDate]);

  useEffect(() => {
    const today = moment().tz(timezone).format('YYYY-MM-DD');
    if (endDate === today) {
      const presets = {
        thisWeek: moment().tz(timezone).startOf('week').format('YYYY-MM-DD'),
        last7days: moment().tz(timezone).subtract(7, 'days').format('YYYY-MM-DD'),
        last14days: moment().tz(timezone).subtract(14, 'days').format('YYYY-MM-DD'),
        last30days: moment().tz(timezone).subtract(30, 'days').format('YYYY-MM-DD'),
        last90days: moment().tz(timezone).subtract(90, 'days').format('YYYY-MM-DD'),
        last6months: moment().tz(timezone).subtract(6, 'months').format('YYYY-MM-DD'),
        last9months: moment().tz(timezone).subtract(9, 'months').format('YYYY-MM-DD'),
        last12months: moment().tz(timezone).subtract(12, 'months').format('YYYY-MM-DD'),
      };
      const found = Object.entries(presets).find(([, val]) => val === startDate);
      setPreset(found ? found[0] : 'custom');
    } else {
      setPreset('custom');
    }
  }, [startDate, endDate, timezone]);

  const handlePresetChange = (event) => {
    const value = event.target.value;
    setPreset(value);
    const end = moment().tz(timezone).format('YYYY-MM-DD');

    logger.info('Selected preset:', value, 'isTritonMember:', isTritonMember);
    
    // Define all presets regardless of user status
    let newStart;
    
    switch(value) {
      case 'thisWeek':
        newStart = moment().tz(timezone).startOf('week').format('YYYY-MM-DD');
        break;
      case 'last7days':
        newStart = moment().tz(timezone).subtract(7, 'days').format('YYYY-MM-DD');
        break;
      case 'last14days':
        newStart = moment().tz(timezone).subtract(14, 'days').format('YYYY-MM-DD');
        break;
      case 'last30days':
        newStart = moment().tz(timezone).subtract(30, 'days').format('YYYY-MM-DD');
        break;
      case 'last90days':
        newStart = moment().tz(timezone).subtract(90, 'days').format('YYYY-MM-DD');
        break;
      case 'last6months':
        if (!isTritonMember) {
          // Fallback to 90 days for non-Triton users
          console.warn('Non-Triton user attempted to select a restricted date range');
          newStart = moment().tz(timezone).subtract(90, 'days').format('YYYY-MM-DD');
          setPreset('last90days');
        } else {
          newStart = moment().tz(timezone).subtract(6, 'months').format('YYYY-MM-DD');
        }
        break;
      case 'last9months':
        if (!isTritonMember) {
          // Fallback to 90 days for non-Triton users
          console.warn('Non-Triton user attempted to select a restricted date range');
          newStart = moment().tz(timezone).subtract(90, 'days').format('YYYY-MM-DD');
          setPreset('last90days');
        } else {
          newStart = moment().tz(timezone).subtract(9, 'months').format('YYYY-MM-DD');
        }
        break;
      case 'last12months':
        if (!isTritonMember) {
          // Fallback to 90 days for non-Triton users
          console.warn('Non-Triton user attempted to select a restricted date range');
          newStart = moment().tz(timezone).subtract(90, 'days').format('YYYY-MM-DD');
          setPreset('last90days');
        } else {
          newStart = moment().tz(timezone).subtract(12, 'months').format('YYYY-MM-DD');
        }
        break;
      case 'custom':
        // Don't update dates for custom selection
        onDateRangeChange(startDate, endDate);
        return;
      default:
        // Don't change dates for unknown values
        return;
    }
    
    logger.info('Setting new date range:', newStart, 'to', end);
    setStartDate(newStart);
    setEndDate(end);
    onDateRangeChange(newStart, end);
  };

  const handleStartDateChange = (e) => {
    const newDate = e.target.value;
    
    // Validate the date doesn't exceed allowed range for non-Triton members
    if (!isTritonMember) {
      const minAllowedDate = moment(endDate).tz(timezone).subtract(90, 'days').format('YYYY-MM-DD');
      if (moment(newDate).isBefore(minAllowedDate)) {
        // Restrict to minimum allowed date if trying to go beyond 90 days
        setStartDate(minAllowedDate);
        setPreset('custom');
        onDateRangeChange(minAllowedDate, endDate);
        return;
      }
    } else {
      // For Triton members, restrict to 12 months maximum
      const minAllowedDate = moment(endDate).tz(timezone).subtract(365, 'days').format('YYYY-MM-DD');
      if (moment(newDate).isBefore(minAllowedDate)) {
        setStartDate(minAllowedDate);
        setPreset('custom');
        onDateRangeChange(minAllowedDate, endDate);
        return;
      }
    }
    
    setStartDate(newDate);
    setPreset('custom');
    onDateRangeChange(newDate, endDate);
  };

  const handleEndDateChange = (e) => {
    const newDate = e.target.value;
    setEndDate(newDate);
    setPreset('custom');
    onDateRangeChange(startDate, newDate);
  };

  return (
    <Box className={styles.dateRangeContainer} sx={{
      p: 2, backgroundColor: 'var(--theme-light-bg)',
      borderRadius: 'var(--theme-border-radius)', mb: 3,
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
      justifyContent: 'flex-start', gap: 2,
    }}>
      <Box sx={{ width: '100%' }}>
        {usingBrowserTimezone && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Using your browser's timezone ({timezone}) since no valid timezone was found in your profile.
          </Alert>
        )}
        <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          Current timezone: <strong style={{ marginLeft: '4px' }}>{timezone}</strong>
          {usingBrowserTimezone && (
            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              (using browser timezone)
            </Typography>
          )}
          <Tooltip title="Update your profile settings to change your timezone for accurate date calculations">
            <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        {!isTritonMember && (
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Standard access allows viewing data up to 90 days old.
          </Typography>
        )}
      </Box>

      <Box sx={{
        width: '100%', display: 'flex', flexDirection: 'row',
        alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'wrap', gap: 1,
      }}>
        <FormControl
          className={styles.dateField}
          sx={{ width: 180 }}
        >
          <InputLabel
            id="date-preset-label"
            shrink
          >
            Date Range
          </InputLabel>
          <Select
            labelId="date-preset-label"
            id="date-preset"
            value={preset}
            onChange={handlePresetChange}
            label="Date Range"
            displayEmpty
            notched
          >
            <MenuItem value="thisWeek">This Week</MenuItem>
            <MenuItem value="last7days">Last 7 Days</MenuItem>
            <MenuItem value="last14days">Last 14 Days</MenuItem>
            <MenuItem value="last30days">Last 30 Days</MenuItem>
            <MenuItem value="last90days">Last 90 Days</MenuItem>
            {isTritonMember && <MenuItem value="last6months">Last 6 Months</MenuItem>}
            {isTritonMember && <MenuItem value="last9months">Last 9 Months</MenuItem>}
            {isTritonMember && <MenuItem value="last12months">Last 12 Months</MenuItem>}
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={handleStartDateChange}
          className={styles.dateField}
          sx={{ width: 180 }}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: endDate,
            min: isTritonMember 
              ? moment(endDate).tz(timezone).subtract(365, 'days').format('YYYY-MM-DD')
              : moment(endDate).tz(timezone).subtract(90, 'days').format('YYYY-MM-DD')
          }}
        />

        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={handleEndDateChange}
          className={styles.dateField}
          sx={{ width: 180 }}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: moment().tz(timezone).format('YYYY-MM-DD'),
            min: startDate
          }}
        />

        <Button
          variant="contained"
          onClick={() => onDateRangeChange(startDate, endDate)}
          className={styles['primary-button']}
          sx={{ height: '40px' }}
        >
          Apply
        </Button>
      </Box>

      {isTritonMember && (
        <Typography variant="body2" sx={{ width: '100%', mt: 1, color: 'success.main' }}>
          As a Triton member, you can access data up to 12 months old.
        </Typography>
      )}
    </Box>
  );
};

export default DateRangeSelector;
